import moment from "moment-timezone";
import Dinero from "dinero.js";

import React, { Fragment, PureComponent } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import {
  Container,
  Segment,
  Tab,
  Table,
  Header,
  Icon
} from "semantic-ui-react";

import SimplePreloader from "../../components/preloaders/SimplePreloader.jsx";
import InvoiceHeader from "../../components/invoices/InvoiceHeader.jsx";
import ResourceIcon from "../../components/resources/resourceIcon/ResourceIcon.jsx";
import ResourceMarker from "../../components/marking/ResourceMarker.jsx";
import LinkWithReset from "../../components/utils/LinkWithReset.jsx";
import InvoiceResourceRow from "../../components/invoices/InvoiceResourceRow.jsx";

import { ROUTES } from "../../constants/routing.js";
import { AT_TYPES } from "../../constants/entity.js";
import { OPERATIONS } from "../../constants/operations.js";
import { COST_FORMAT } from "../../components/config.js";
import { ICON_MAP } from "../../constants/iconMap.js";

import { getInvoice } from "../../state/reducers/invoices/invoiceActions.js";
import { getDownloadPDF } from "../../state/reducers/downloads/downloadActions.js";

import invoiceSelector from "../../state/reducers/invoices/invoiceSelector.js";
import endpointSelector from "../../state/reducers/endpoints/endpointSelector.js";

import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class Invoice extends PureComponent {
  static propTypes = {
    invoice: PropTypes.object,
    isFetching: PropTypes.bool.isRequired,
    listInvoiceAvailability: PropTypes.object.isRequired,
    getInvoice: PropTypes.func.isRequired,
    getDownloadPDF: PropTypes.func.isRequired,
    copyKey: PropTypes.string.isRequired,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    copyKey: "invoice"
  };

  constructor(props) {
    super(props);

    const id = props.match.params.id;

    this.state = {
      id: `${ROUTES.INVOICES}/${id}`
    };
  }

  componentDidMount = () => {
    const { listInvoiceAvailability, getInvoice } = this.props;

    if (listInvoiceAvailability.available) {
      getInvoice(this.state.id);
    }
  };

  // -- FILE DOWNLOADS -- //

  onPDFClick = () => {
    const { invoice, listInvoiceAvailability, getDownloadPDF, copyKey, copy } =
      this.props;

    // unpadded: 1 - 9
    var weekNumber = moment(invoice.startAt).utc().format("W");

    // padded: 01 - 09
    if (weekNumber <= 9) {
      weekNumber = ("0" + weekNumber).slice(-2);
    }

    if (listInvoiceAvailability.available) {
      const fileName = `week-${weekNumber}-${copy(
        `${copyKey}.download_file_name`
      )}-${invoice.number}`;

      getDownloadPDF(invoice["@id"], fileName);
    }
  };

  // -- RENDER -- //

  renderProjectRows = (data, showUsage) => {
    if (!data) return null;

    return data.map(item => {
      const icon = (
        <LinkWithReset to={item["@id"]} className={"resource-icon"}>
          <Icon name="folder" /> {item.name}
        </LinkWithReset>
      );

      return (
        <InvoiceResourceRow
          data={item}
          key={item["@id"]}
          icon={icon}
          showUsage={showUsage}
        />
      );
    });
  };

  renderServiceRows = (data, showUsage) => {
    const { copy } = this.props;

    if (!data) return null;

    return data.map(item => {
      let iconData = Object.assign({}, item);
      iconData.name = copy(
        `resource_types.plural.${item["@type"].toLowerCase()}`
      );

      const icon = (
        <ResourceIcon data={iconData} size="large" disabled={true} />
      );

      return (
        <InvoiceResourceRow
          data={item}
          key={item["@id"]}
          icon={icon}
          showUsage={showUsage}
        />
      );
    });
  };

  // could just use a generalised version of the project tabs if I can manage icons somewhere?
  renderImageRows = (data, showUsage) => {
    if (!data) return null;

    return data.map(item => {
      const icon = (
        <LinkWithReset to={item["@id"]} className="table-link">
          <Icon name={ICON_MAP[item.operatingSystem]} /> {item.name}
        </LinkWithReset>
      );

      return (
        <InvoiceResourceRow
          data={item}
          key={item["@id"]}
          icon={icon}
          showUsage={showUsage}
        />
      );
    });
  };

  renderTab = (data, rowRenderer, tabCopy, showUsage = true) => {
    const { copy } = this.props;

    if (!data)
      return (
        <Tab.Pane>
          <p>{tabCopy.no_data}</p>
        </Tab.Pane>
      );

    return (
      <Tab.Pane>
        <p>{tabCopy.body}</p>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{tabCopy.name}</Table.HeaderCell>
              {showUsage && (
                <Table.HeaderCell>
                  {copy("invoice.tabs.usage")}
                </Table.HeaderCell>
              )}
              <Table.HeaderCell>{copy("invoice.tabs.charge")}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>{rowRenderer(data, showUsage)}</Table.Body>
        </Table>
      </Tab.Pane>
    );
  };

  renderPanes = () => {
    const { invoice, copy } = this.props;

    return [
      {
        menuItem: copy("invoice.tabs.by_project.title"),
        render: () =>
          this.renderTab(
            invoice.projects,
            this.renderProjectRows,
            copy("invoice.tabs.by_project")
          )
      },
      {
        menuItem: copy("invoice.tabs.by_service.title"),
        render: () =>
          this.renderTab(
            invoice.services,
            this.renderServiceRows,
            copy("invoice.tabs.by_service")
          )
      },
      {
        menuItem: copy("invoice.tabs.by_image.title"),
        render: () =>
          this.renderTab(
            invoice.images,
            this.renderImageRows,
            copy("invoice.tabs.by_image"),
            false
          )
      }
    ];
  };

  renderSummary = () => {
    const { invoice, copyKey, copy } = this.props;

    if (!invoice) return null;

    const summary = invoice.summary;
    const currency = summary.currency;

    return (
      <Fragment>
        <Segment className="clear-both">
          <Header as="h4" className="float-left">
            {copy(`${copyKey}.week`)}{" "}
            {moment(invoice.startAt).utc().format("W")}
            {"* "}
            {copy(`${copyKey}.week_summary`)}
          </Header>

          <p className="float-right">
            <strong> {copy(`${copyKey}.date`)}: </strong>
            {`${moment(invoice.startAt).utc().format("DD/MM/YYYY")} ${copy(
              `${copyKey}.date_separator`
            )} ${moment(invoice.endAt).utc().format("DD/MM/YYYY")}`}
            {"*"}
          </p>

          {typeof summary === "object" && (
            <Table celled unstackable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    {copy(`${copyKey}.description`)}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {copy(`${copyKey}.value`)}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {copy(`${copyKey}.running_total`)}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {typeof summary.subtotal === "number" && (
                  <Table.Row>
                    <Table.Cell colSpan={2}>
                      <strong>{copy(`${copyKey}.before_modifiers`)}</strong>
                    </Table.Cell>
                    <Table.Cell>
                      {Dinero({
                        amount: Math.round(summary.subtotal),
                        currency: currency
                      }).toFormat(COST_FORMAT)}
                    </Table.Cell>
                  </Table.Row>
                )}
                {typeof summary.vouchers === "number" && (
                  <Table.Row>
                    <Table.Cell>
                      <strong>{copy(`${copyKey}.vouchers`)}</strong>
                    </Table.Cell>
                    <Table.Cell>
                      {Dinero({
                        amount: Math.round(summary.vouchers),
                        currency: currency
                      }).toFormat(COST_FORMAT)}
                    </Table.Cell>
                    <Table.Cell>
                      {Dinero({
                        amount: Math.round(summary.totalAfterVouchers),
                        currency: currency
                      }).toFormat(COST_FORMAT)}
                    </Table.Cell>
                  </Table.Row>
                )}
                {typeof summary.discount === "number" && (
                  <Table.Row>
                    <Table.Cell>
                      <strong>{copy(`${copyKey}.discount`)}</strong>
                    </Table.Cell>
                    <Table.Cell>{`${summary.discount}%`}</Table.Cell>
                    <Table.Cell>
                      {Dinero({
                        amount: Math.round(summary.totalAfterDiscount),
                        currency: currency
                      }).toFormat(COST_FORMAT)}
                    </Table.Cell>
                  </Table.Row>
                )}
                {typeof summary.VAT === "number" && (
                  <Table.Row>
                    <Table.Cell>
                      <strong>{copy(`${copyKey}.vat`)}</strong>
                    </Table.Cell>
                    <Table.Cell>{`@${summary.VAT}%`}</Table.Cell>
                    <Table.Cell>
                      {Dinero({
                        amount: Math.round(summary.totalAfterVAT),
                        currency: currency
                      }).toFormat(COST_FORMAT)}
                    </Table.Cell>
                  </Table.Row>
                )}
                {typeof summary.total === "number" && (
                  <Table.Row>
                    <Table.Cell colSpan={2}>
                      <strong>{copy(`${copyKey}.total`)}</strong>
                    </Table.Cell>
                    <Table.Cell>
                      {Dinero({
                        amount: Math.round(summary.total),
                        currency: currency
                      }).toFormat(COST_FORMAT)}
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          )}
        </Segment>
        <p className="mini-text float-right">
          *{copy(`${copyKey}.date_details`)}
        </p>
      </Fragment>
    );
  };

  render() {
    const {
      invoice,
      isFetching,
      listInvoiceAvailability: { permitted, enabled },
      copyKey,
      copy
    } = this.props;

    if (!permitted) {
      return <UnauthorisedFeature title={copy(`${copyKey}.title`)} />;
    }

    // early return if project isn't ready to render
    if (isFetching) {
      return <SimplePreloader />;
    }

    if (!enabled || !invoice) {
      return (
        <Container>
          <h1>{copy(`${copyKey}.title`)}</h1>
          <p>{copy(`${copyKey}.empty`)}</p>
        </Container>
      );
    }

    return (
      <Container>
        <InvoiceHeader canViewInvoices={true} onPDF={this.onPDFClick} />

        <Header as="h2" className="float-left">
          {`${copy(`${copyKey}.invoice`)}: ${invoice.number}`}
        </Header>

        <ResourceMarker marking={invoice.marking} className="project-marker" />

        {this.renderSummary()}

        <Tab panes={this.renderPanes()} />
      </Container>
    );
  }
}

const mapStateToProps = (state, props) => ({
  invoice: invoiceSelector.selectInvoice()(state, props),
  isFetching: state.invoices.isFetching,
  listInvoiceAvailability: endpointSelector.selectOperationAvailability(state)(
    AT_TYPES.INVOICE,
    OPERATIONS.LIST
  ),
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  getInvoice: id => dispatch(getInvoice(id)),
  getDownloadPDF: (id, fileName) => dispatch(getDownloadPDF(id, fileName))
});

export default connect(mapStateToProps, mapDispatchToProps)(Invoice);
